import type { WorkspaceNode } from "../../../types/workspace";

/** Account root card (logo only). */
export const WORKSPACE_ACCOUNT_CARD_SIZE = 108;
/** Inscribed square side so a 45° rotation fits the outer card bounds. */
export const WORKSPACE_ACCOUNT_CARD_INNER_SIZE = Math.round(
  WORKSPACE_ACCOUNT_CARD_SIZE / Math.SQRT2
);

/** Forward "Add report / Add simulation" diamonds — smaller than account for hierarchy. */
export const WORKSPACE_PLACEHOLDER_NODE_SIZE = 52;
export const WORKSPACE_PLACEHOLDER_NODE_INNER_SIZE = Math.round(
  WORKSPACE_PLACEHOLDER_NODE_SIZE / Math.SQRT2
);

/**
 * Drag snap grid (px). Matches canvas Background dots so free moves still
 * land on a uniform lattice for column/row alignment.
 */
export const WORKSPACE_NODE_SNAP_GRID = 22;

/** Max visible title characters before an ellipsis is appended. */
export const WORKSPACE_NODE_CARD_TITLE_MAX_LENGTH = 25;

function snapWorkspaceCardDimension(px: number): number {
  return Math.ceil(px / WORKSPACE_NODE_SNAP_GRID) * WORKSPACE_NODE_SNAP_GRID;
}

/** Hub-parity pipeline card — snap-aligned width. */
export const WORKSPACE_NODE_CARD_WIDTH = snapWorkspaceCardDimension(352);

export const WORKSPACE_NODE_CARD_HEIGHT = 154;

/** @deprecated Use WORKSPACE_NODE_CARD_* — kept for imports during transition. */
export const WORKSPACE_NODE_COLUMN_WIDTH = WORKSPACE_NODE_CARD_WIDTH;
/** @deprecated Use WORKSPACE_NODE_CARD_* — kept for imports during transition. */
export const WORKSPACE_NODE_COLUMN_HEIGHT = WORKSPACE_NODE_CARD_HEIGHT;

/** Minimum clear space kept between node bounding boxes while dragging. */
export const WORKSPACE_NODE_COLLISION_GAP = 28;

/**
 * Uniform runway between every pipeline column (account → strategy → report → …).
 * Snap-grid multiple so Organize columns stay on the lattice.
 */
export const WORKSPACE_GRAPH_COLUMN_GAP = WORKSPACE_NODE_SNAP_GRID * 3; // 66
/**
 * Stack gap for siblings within a branch (sims, policies under one report).
 * Multiple of the snap grid so card height + gap stays lattice-aligned.
 */
export const WORKSPACE_GRAPH_ROW_GAP = WORKSPACE_NODE_SNAP_GRID * 2; // 44
/**
 * Gap between account-level branches. Also the base gap used when separating
 * report lineages that have sims/policies (see report-lineage packing).
 */
export const WORKSPACE_GRAPH_BRANCH_GAP = WORKSPACE_NODE_SNAP_GRID * 3; // 66

/**
 * Vertical air between different reports’ sim/policy stacks — larger than the
 * in-branch row gap so lineages read as separate groups.
 */
export const WORKSPACE_GRAPH_REPORT_LINEAGE_GAP = WORKSPACE_NODE_SNAP_GRID * 5; // 110

/**
 * Bump when Organize / strategy auto-layout packing rules change so open
 * canvases re-tidy instead of keeping a prior sparse fingerprint.
 */
export const WORKSPACE_LAYOUT_REVISION = 17;
/**
 * Add (+) placeholders use the same column runway as real next-step cards
 * so empty parents and populated columns share one spacing language.
 */
export const WORKSPACE_PLACEHOLDER_COLUMN_GAP = WORKSPACE_GRAPH_COLUMN_GAP;
export const WORKSPACE_GRAPH_ORIGIN_X = 40;
export const WORKSPACE_GRAPH_ORIGIN_Y = 88;

/** Horizontal step between parallel edge verticals leaving the same source. */
export const WORKSPACE_EDGE_LANE_STEP = 16;
export const WORKSPACE_EDGE_STUB_BASE = 28;

export function snapWorkspaceNodePosition(
  position: { x: number; y: number },
  grid: number = WORKSPACE_NODE_SNAP_GRID
): { x: number; y: number } {
  if (!Number.isFinite(grid) || grid <= 0) return { x: position.x, y: position.y };
  return {
    x: Math.round(position.x / grid) * grid,
    y: Math.round(position.y / grid) * grid,
  };
}

/**
 * Snap by the node center instead of the top-left. Needed for the account
 * diamond (72px) on a 22px dot grid — top-left snapping sits the apex on a
 * row of dots while leaving a gap at the bottom.
 */
export function snapWorkspaceNodeByCenter(
  position: { x: number; y: number },
  size: { width: number; height: number },
  grid: number = WORKSPACE_NODE_SNAP_GRID
): { x: number; y: number } {
  if (!Number.isFinite(grid) || grid <= 0) return { x: position.x, y: position.y };
  const centerX = position.x + size.width / 2;
  const centerY = position.y + size.height / 2;
  return {
    x: Math.round(centerX / grid) * grid - size.width / 2,
    y: Math.round(centerY / grid) * grid - size.height / 2,
  };
}

/** @deprecated Computed per-column in layout — kept for legacy imports. */
export const WORKSPACE_GRAPH_DEPTH_X = WORKSPACE_NODE_CARD_WIDTH + WORKSPACE_GRAPH_COLUMN_GAP;
/** @deprecated Computed per-column in layout — kept for legacy imports. */
export const WORKSPACE_GRAPH_SIBLING_Y = WORKSPACE_NODE_CARD_HEIGHT + WORKSPACE_GRAPH_ROW_GAP;

export function getWorkspaceNodeDimensions(node: Pick<WorkspaceNode, "type">): {
  width: number;
  height: number;
} {
  if (node.type === "account") {
    return {
      width: WORKSPACE_ACCOUNT_CARD_SIZE,
      height: WORKSPACE_ACCOUNT_CARD_SIZE,
    };
  }
  return {
    width: WORKSPACE_NODE_CARD_WIDTH,
    height: WORKSPACE_NODE_CARD_HEIGHT,
  };
}
